import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuditService } from 'src/app/core/audit-management/audit.service';
import { AuditLog } from 'src/app/shared/models/AuditLog';

interface FieldChange {
  field: string;
  label: string;
  before: any;
  after: any;
}

interface DevisHistoryItem {
  log: AuditLog;
  date: Date;
  projet?: string;
  statutAvant?: string;
  statutApres?: string;
  motifRefus?: string;
  motifRetour?: string;
  changes: FieldChange[];
}

@Component({
  selector: 'app-devis-history',
  templateUrl: './devis-history.component.html',
  styleUrl: './devis-history.component.less'
})
export class DevisHistoryComponent {

  isLoading = true;
  items: DevisHistoryItem[] = [];
  devisReference = '';

  private fieldLabels: Record<string, string | null> = {
    // Champs affichables
    devisStatut:              'Statut',
    devisMontantTotal:        'Montant total',
    devisPrixUoPropose:       'Prix UO proposé',
    devisPrixUoGrille:        'Prix UO grille',
    devisDuree:               'Durée prévisionnelle',
    devisProvisionFrais:      'Provision frais',
    devisDateDemarrageGo:     'Date démarrage GO',
    devisDateFinTravaux:      'Date fin travaux',
    // ── Date d'entretien ──────────────────────────────────
    dateEntretien:            'Date d\'entretien',
    devisDateEntretien:       'Date d\'entretien',
    devisNumeroBdc:           'N° BDC',
    devisNomContrat:          'Nom du contrat',
    devisNumeroContrat:       'N° Contrat',
    devisCommentaire:         'Commentaire CDC',
    devisMajore:              'Prestation majorée',
    devisMotifRefus:          'Motif de refus',
    devisMotifRetourArriere:  'Motif retour en arrière',
    devisReference:           'N° Devis',
    devisBeneficiaire:        'Bénéficiaire',

    // Champs FEB
    febLabel:                    'Label FEB',
    febNomenclature:             'Nomenclature',
    febPcSncf:                   'PC SNCF fourni',
    febPresenceSiteSncf:         'Présence site SNCF',
    febContexteObjectifProjet:   'Contexte et objectif',
    febProjectNom:               'Projet',
    febLocalisationLabel:        'Localisation',
    febEntiteSncfNom:            'Entité SNCF',
    febDivisionDemandeurNom:     'Division demandeur',
    febUoActivitePressentiLabel: 'Activité UO',

    // Activités (sous-champs)
    label:      'Label activité',
    pourcentage:'Répartition (%)',

    // Masqués
    devisId:              null,
    devisCreatedAt:       null,
    devisUpdatedAt:       null,
    febId:                null,
    demandId:             null,
    activiteFibId:        null,
    febPrixUoGrille:      null,
    febPrixUoGrilleMajore:null,
    id:                   null,
    version:              null,
    createdAt:            null,
    updatedAt:            null,
  };

  private separatedFields = ['devisStatut', 'devisMotifRefus', 'devisMotifRetourArriere'];

  private statutLabels: Record<string, string> = {
    CREE:       'Créé',
    EN_ATTENTE: 'En attente',
    ACCEPTE:    'Accepté',
    REFUSE:     'Refusé',
    ARCHIVE:    'Archivé',
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { devisId: string; devisReference?: string },
    public dialogRef: MatDialogRef<DevisHistoryComponent>,
    private auditService: AuditService
  ) {
    this.devisReference = data.devisReference ?? data.devisId;
    this.loadHistory();
  }

  loadHistory(): void {
    this.auditService.getAuditHistory('DEVIS', this.data.devisId).subscribe({
      next: (logs: AuditLog[]) => {
        this.items = logs.map(log => this.toHistoryItem(log));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  private parseJson(s: any): any {
    if (!s) return null;
    if (typeof s === 'string') {
      try { return JSON.parse(s); } catch { return null; }
    }
    return s;
  }

  private toHistoryItem(log: AuditLog): DevisHistoryItem {
    const changedRaw = this.parseJson(log.changedFields);

    let allChanges: { field: string; before: any; after: any }[] = [];

    if (Array.isArray(changedRaw)) {
      allChanges = changedRaw.filter(c => c?.field);
    } else if (changedRaw && typeof changedRaw === 'object') {
      const before = this.parseJson(log.snapshotBefore) ?? {};
      const after  = this.parseJson(log.snapshotAfter)  ?? {};
      allChanges = Object.keys(changedRaw).map(f => ({
        field:  f,
        before: before?.[f],
        after:  after?.[f]
      }));
    }

    const find = (field: string) => allChanges.find(c => c.field === field);

    const statutChange = find('devisStatut');
    const motifRefus   = find('devisMotifRefus');
    const motifRetour  = find('devisMotifRetourArriere');

    const changes: FieldChange[] = allChanges
      .filter(c => !this.separatedFields.includes(c.field))
      .map(c => {
        const label = this.humanizeField(c.field);
        if (label === null) return null;
        return {
          field:  c.field,
          label,
          before: this.formatValue(c.field, c.before),
          after:  this.formatValue(c.field, c.after),
        };
      })
      .filter((c): c is FieldChange => c !== null);

    return {
      log,
      date:        new Date(log.createdAt),
      projet:      (log as any).projet,
      statutAvant: statutChange
        ? (this.statutLabels[statutChange.before] ?? statutChange.before)
        : undefined,
      statutApres: statutChange
        ? (this.statutLabels[statutChange.after] ?? statutChange.after)
        : undefined,
      motifRefus:  motifRefus?.after  ?? undefined,
      motifRetour: motifRetour?.after ?? undefined,
      changes,
    };
  }

  private humanizeField(field: string): string | null {
    if (Object.prototype.hasOwnProperty.call(this.fieldLabels, field)) {
      return this.fieldLabels[field] as string | null;
    }

    const actMatch = field.match(/^febActivites\[(\d+)\]\.(.+)$/);
    if (actMatch) {
      const index    = parseInt(actMatch[1]) + 1;
      const subField = actMatch[2];
      const subLabel = this.fieldLabels[subField];
      if (subLabel === null) return null;
      return `Activité ${index} — ${subLabel ?? subField}`;
    }

    const idMatch = field.match(/\[id=[^\]]+\]\.(.+)$/);
    if (idMatch) {
      const subField = idMatch[1];
      const subLabel = this.fieldLabels[subField];
      if (subLabel === null) return null;
      return subLabel ?? subField;
    }

    const dotIdx = field.lastIndexOf('.');
    if (dotIdx !== -1) {
      const leaf      = field.substring(dotIdx + 1);
      const leafLabel = this.fieldLabels[leaf];
      if (leafLabel === null) return null;
      return leafLabel ?? leaf;
    }

    return field;
  }

  private formatValue(field: string, value: any): any {
    if (value === null || value === undefined || value === '') return null;

    if (field === 'devisStatut' && typeof value === 'string') {
      return this.statutLabels[value] ?? value;
    }

    if (['devisMontantTotal', 'devisPrixUoPropose', 'devisProvisionFrais',
         'devisPrixUoGrille'].includes(field)) {
      const n = Number(value);
      if (!isNaN(n)) return n.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €';
    }

    if (field === 'devisDuree') {
      const n = Number(value);
      if (!isNaN(n)) return n + ' j';
    }

    if (field === 'pourcentage' || field.includes('pourcentage')) {
      const n = Number(value);
      if (!isNaN(n)) return n + ' %';
    }

    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    if (value === 'true')  return 'Oui';
    if (value === 'false') return 'Non';

    // Dates ISO (inclut dateEntretien) → format FR
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toLocaleDateString('fr-FR');
    }

    return value;
  }

  // ── Helpers template ──────────────────────────────────────────────────────

  isStatusChange(item: DevisHistoryItem): boolean {
    return !!item.statutApres && item.statutAvant !== item.statutApres;
  }

  operationLabel(op: string): string {
    const m: Record<string, string> = {
      CREATE: 'Création', UPDATE: 'Modification', DELETE: 'Suppression'
    };
    return m[op] ?? op;
  }

  operationIcon(op: string): string {
    const m: Record<string, string> = {
      CREATE: 'add_circle', UPDATE: 'edit', DELETE: 'delete'
    };
    return m[op] ?? 'info';
  }

  operationColor(op: string): string {
    const m: Record<string, string> = {
      CREATE: '#2E7D32', UPDATE: '#1565C0', DELETE: '#E64A19'
    };
    return m[op] ?? '#616161';
  }

  statutColor(label?: string): string {
    if (!label) return '#616161';
    const map: Record<string, string> = {
      CREE:       '#616161', 'Créé':      '#616161',
      EN_ATTENTE: '#1565C0', 'En attente':'#1565C0',
      ACCEPTE:    '#2E7D32', 'Accepté':   '#2E7D32',
      REFUSE:     '#E64A19', 'Refusé':    '#E64A19',
      ARCHIVE:    '#795548', 'Archivé':   '#795548',
    };
    return map[label] ?? '#616161';
  }
}